import React, { useState } from 'react';
import { SEOHead } from '../seo';

function TherapistProfilePageDemo() {
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    preferredContact: 'email'
  });

  // Mock therapist data
  const therapist = {
    _id: 'demo123',
    firstName: 'Dr. Sarah',
    lastName: 'Johnson',
    title: 'Licensed Clinical Social Worker (LCSW)',
    practiceName: 'Mindful Healing Therapy',
    profilePhoto: 'http://counselingunconditionally.com/wp-content/uploads/2025/02/Courtney-Headshot-4.png',
    location: {
      city: 'San Francisco',
      state: 'California',
      address: '123 Market Street'
    },
    professional: {
      yearsExperience: 8,
      licenseNumber: 'LCSW12345',
      licenseState: 'CA',
      education: [
        'Master of Social Work - UC Berkeley',
        'Bachelor of Psychology - Stanford University'
      ],
      certifications: [
        'Certified in Cognitive Behavioral Therapy (CBT)',
        'EMDR Level II Certified',
        'Trauma-Informed Care Specialist'
      ],
      languages: ['English', 'Spanish']
    },
    specializations: [
      'Anxiety Disorders',
      'Depression',
      'Trauma & PTSD',
      'Relationship Issues',
      'Life Transitions'
    ],
    treatmentApproaches: [
      'Cognitive Behavioral Therapy (CBT)',
      'EMDR',
      'Mindfulness-Based Therapy',
      'Somatic Therapy'
    ],
    ageGroups: ['Adults', 'Young Adults'],
    sessionTypes: ['in-person', 'telehealth'],
    insuranceAccepted: [
      'Blue Cross Blue Shield',
      'Aetna',
      'Cigna',
      'UnitedHealth',
      'Kaiser Permanente'
    ],
    rates: {
      individual: 150,
      couples: 200,
      slidingScale: true,
      freeConsultation: true
    },
    bio: `I believe that everyone deserves to live a life filled with purpose, connection, and joy. As a licensed clinical social worker with over 8 years of experience, I specialize in helping individuals navigate life's challenges with compassion and evidence-based approaches.

My practice is rooted in the belief that therapy should be a collaborative process. I work with my clients to create a safe, non-judgmental space where healing and growth can occur naturally. Whether you're dealing with anxiety, depression, trauma, or major life transitions, I'm here to support you on your journey.

I have extensive training in trauma-informed care and use a variety of therapeutic modalities including CBT, EMDR, and mindfulness-based approaches. My goal is to help you develop the tools and insights needed to create lasting positive change in your life.`,
    approachDescription: `My therapeutic approach is integrative and client-centered. I believe that you are the expert on your own life, and my role is to provide you with the tools, support, and insights needed to achieve your goals.

I draw from several evidence-based modalities:
• Cognitive Behavioral Therapy (CBT) to help identify and change unhelpful thought patterns
• EMDR for trauma processing and healing
• Mindfulness techniques to increase present-moment awareness
• Somatic approaches to address the mind-body connection

Each session is tailored to your unique needs and goals. I believe in meeting you exactly where you are and moving at a pace that feels comfortable and sustainable for you.`,
    whatToExpected: `In our first session, we'll spend time getting to know each other and discussing what brings you to therapy. I'll ask about your goals, your history, and what you hope to achieve through our work together.

Subsequent sessions typically involve:
• Check-ins about your week and any immediate concerns
• Working on specific goals and strategies
• Processing emotions and experiences
• Learning and practicing new coping skills
• Regular reflection on your progress

I believe in creating a warm, supportive environment where you feel heard and understood. You can expect our sessions to be collaborative, with plenty of space for your questions and feedback about what's working best for you.`,
    verification: {
      isVerified: true,
      verificationDate: new Date('2023-01-15')
    }
  };

  const handleContactFormSubmit = (e) => {
    e.preventDefault();
    alert('This is a demo! In the real version, your message would be sent to the therapist.');
    setShowContactForm(false);
    setContactForm({ name: '', email: '', phone: '', message: '', preferredContact: 'email' });
  };

  const handleContactFormChange = (e) => {
    const { name, value } = e.target;
    setContactForm(prev => ({ ...prev, [name]: value }));
  };

  const formatYearsExperience = (years) => {
    if (!years) return 'Not specified';
    if (typeof years === 'string') return years;
    return `${years} years`;
  };

  const formatRate = (rate) => {
    if (!rate) return 'Contact for pricing';
    return `$${rate}/session`;
  };

  const seoProps = {
    title: `${therapist.firstName} ${therapist.lastName} - ${therapist.specializations[0]} Therapist | TherapyList`,
    description: `Book an appointment with ${therapist.firstName} ${therapist.lastName}, a licensed ${therapist.specializations[0]} therapist in ${therapist.location.city}. ${therapist.bio.substring(0, 150)}...`,
    keywords: `${therapist.firstName} ${therapist.lastName}, ${therapist.specializations[0]}, therapist, ${therapist.location.city}, mental health, therapy`,
    canonical: `${window.location.origin}/therapist/demo`,
  };

  return (
    <>
      <SEOHead {...seoProps} />
      
      <div className="bg-[#e4fde1] min-h-screen" style={{ marginTop: '-200px', paddingTop: '200px' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          
          {/* Demo Banner */}
          <div className="bg-[#9dacff] text-white px-6 py-4 rounded-xl mb-8 text-center">
            <p className="text-lg font-semibold">
              🎨 Demo Profile Page - This is how your therapist profiles will look!
            </p>
          </div>
          
          {/* Header Section */}
          <div className="bg-white rounded-3xl p-8 lg:p-12 shadow-xl border border-[#254e70]/10 mb-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
              
              {/* Profile Photo */}
              <div className="lg:col-span-1">
                <div className="relative">
                  <div className="absolute -inset-1 bg-[#9dacff] rounded-3xl opacity-75"></div>
                  <div className="relative rounded-3xl overflow-hidden shadow-2xl ring-1 ring-white/20 bg-white p-1">
                    <img 
                      src={therapist.profilePhoto} 
                      alt={`${therapist.firstName} ${therapist.lastName}`}
                      className="w-full aspect-[4/5] object-cover rounded-2xl"
                    />
                  </div>
                  
                  {/* Verification Badge */}
                  {therapist.verification?.isVerified && (
                    <div className="absolute top-4 right-4 bg-green-500 text-white p-2 rounded-full shadow-lg">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>
              </div>

              {/* Main Info */}
              <div className="lg:col-span-2 space-y-6">
                {/* Name and Title */}
                <div>
                  <h1 className="text-4xl lg:text-5xl font-bold text-[#254e70] mb-2 leading-tight" style={{ fontFamily: 'Kanit Medium' }}>
                    {therapist.firstName} {therapist.lastName}
                  </h1>
                  {therapist.title && (
                    <p className="text-xl text-[#254e70]/70 mb-4">{therapist.title}</p>
                  )}
                  {therapist.practiceName && (
                    <p className="text-lg text-[#9dacff] font-medium">{therapist.practiceName}</p>
                  )}
                </div>

                {/* Key Info Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 p-4 bg-[#9dacff]/10 rounded-xl">
                    <svg className="w-5 h-5 text-[#9dacff]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <div>
                      <p className="text-sm text-[#254e70]/60">Location</p>
                      <p className="font-medium text-[#254e70]">
                        {therapist.location?.city}, {therapist.location?.state}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-4 bg-[#9dacff]/10 rounded-xl">
                    <svg className="w-5 h-5 text-[#9dacff]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <p className="text-sm text-[#254e70]/60">Experience</p>
                      <p className="font-medium text-[#254e70]">
                        {formatYearsExperience(therapist.professional?.yearsExperience)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-4 bg-[#9dacff]/10 rounded-xl">
                    <svg className="w-5 h-5 text-[#9dacff]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <p className="text-sm text-[#254e70]/60">License</p>
                      <p className="font-medium text-[#254e70]">
                        {therapist.professional?.licenseNumber ? 'Licensed' : 'License Pending'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-4 bg-[#9dacff]/10 rounded-xl">
                    <svg className="w-5 h-5 text-[#9dacff]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                    </svg>
                    <div>
                      <p className="text-sm text-[#254e70]/60">Individual Rate</p>
                      <p className="font-medium text-[#254e70]">
                        {formatRate(therapist.rates?.individual)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Contact Button */}
                <button
                  onClick={() => setShowContactForm(true)}
                  className="w-full sm:w-auto bg-[#254e70] text-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-[#1d3e5a] hover:-translate-y-0.5 transition-all duration-200 inline-flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  Contact {therapist.firstName}
                </button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              
              {/* Bio Section */}
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-[#254e70]/10">
                <h2 className="text-2xl font-bold text-[#254e70] mb-4">About {therapist.firstName}</h2>
                <p className="text-[#254e70]/80 leading-relaxed whitespace-pre-line">
                  {therapist.bio}
                </p>
              </div>

              {/* Specializations */}
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-[#254e70]/10">
                <h2 className="text-2xl font-bold text-[#254e70] mb-4">Specializations</h2>
                <div className="flex flex-wrap gap-3">
                  {therapist.specializations.map((specialty, index) => (
                    <span 
                      key={index}
                      className="px-4 py-2 bg-[#9dacff]/10 text-[#254e70] rounded-lg border border-[#9dacff]/20 font-medium"
                    >
                      {specialty}
                    </span>
                  ))}
                </div>
              </div>

              {/* Treatment Approaches */}
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-[#254e70]/10">
                <h2 className="text-2xl font-bold text-[#254e70] mb-4">Treatment Approaches</h2>
                <div className="flex flex-wrap gap-3">
                  {therapist.treatmentApproaches.map((approach, index) => (
                    <span 
                      key={index}
                      className="px-4 py-2 bg-[#254e70]/10 text-[#254e70] rounded-lg font-medium"
                    >
                      {approach}
                    </span>
                  ))}
                </div>
              </div>

              {/* What to Expect */}
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-[#254e70]/10">
                <h2 className="text-2xl font-bold text-[#254e70] mb-4">What to Expect</h2>
                <p className="text-[#254e70]/80 leading-relaxed whitespace-pre-line">
                  {therapist.whatToExpected}
                </p>
              </div>

              {/* Approach Description */}
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-[#254e70]/10">
                <h2 className="text-2xl font-bold text-[#254e70] mb-4">My Approach</h2>
                <p className="text-[#254e70]/80 leading-relaxed whitespace-pre-line">
                  {therapist.approachDescription}
                </p>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1 space-y-6">
              
              {/* Quick Info Card */}
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-[#254e70]/10 sticky top-6">
                <h3 className="text-xl font-bold text-[#254e70] mb-4">Quick Info</h3>
                
                <div className="space-y-4">
                  {/* Session Types */}
                  <div>
                    <p className="text-sm text-[#254e70]/60 mb-1">Session Types</p>
                    <div className="flex flex-wrap gap-2">
                      {therapist.sessionTypes.map((type, index) => (
                        <span key={index} className="text-xs px-2 py-1 bg-[#9dacff]/10 text-[#254e70] rounded-md">
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Age Groups */}
                  <div>
                    <p className="text-sm text-[#254e70]/60 mb-1">Age Groups</p>
                    <div className="flex flex-wrap gap-2">
                      {therapist.ageGroups.map((age, index) => (
                        <span key={index} className="text-xs px-2 py-1 bg-[#254e70]/10 text-[#254e70] rounded-md">
                          {age}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Languages */}
                  <div>
                    <p className="text-sm text-[#254e70]/60 mb-1">Languages</p>
                    <p className="text-[#254e70] font-medium">
                      {therapist.professional.languages.join(', ')}
                    </p>
                  </div>

                  {/* Rates */}
                  <div>
                    <p className="text-sm text-[#254e70]/60 mb-2">Session Rates</p>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span>Individual:</span>
                        <span className="font-medium">${therapist.rates.individual}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Couples:</span>
                        <span className="font-medium">${therapist.rates.couples}</span>
                      </div>
                      {therapist.rates.slidingScale && (
                        <p className="text-green-600 text-xs mt-2">✓ Sliding scale available</p>
                      )}
                      {therapist.rates.freeConsultation && (
                        <p className="text-green-600 text-xs">✓ Free consultation offered</p>
                      )}
                    </div>
                  </div>

                  {/* Insurance */}
                  <div>
                    <p className="text-sm text-[#254e70]/60 mb-1">Insurance Accepted</p>
                    <div className="flex flex-wrap gap-1">
                      {therapist.insuranceAccepted.slice(0, 3).map((insurance, index) => (
                        <span key={index} className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-md">
                          {insurance}
                        </span>
                      ))}
                      <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-md">
                        +{therapist.insuranceAccepted.length - 3} more
                      </span>
                    </div>
                  </div>
                </div>

                {/* Contact Button */}
                <button
                  onClick={() => setShowContactForm(true)}
                  className="w-full mt-6 bg-[#9dacff] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#8299ff] transition-colors duration-200"
                >
                  Send Message
                </button>
              </div>

              {/* Education & Credentials */}
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-[#254e70]/10">
                <h3 className="text-xl font-bold text-[#254e70] mb-4">Education & Credentials</h3>
                
                <div className="mb-4">
                  <p className="text-sm text-[#254e70]/60 mb-2">Education</p>
                  <ul className="space-y-1">
                    {therapist.professional.education.map((edu, index) => (
                      <li key={index} className="text-sm text-[#254e70]">
                        • {typeof edu === 'string' ? edu : edu.degree || edu}
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <p className="text-sm text-[#254e70]/60 mb-2">Certifications</p>
                  <ul className="space-y-1">
                    {therapist.professional.certifications.map((cert, index) => (
                      <li key={index} className="text-sm text-[#254e70]">
                        • {typeof cert === 'string' ? cert : cert.name || cert}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Form Modal */}
      {showContactForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-[#254e70]">Contact {therapist.firstName}</h3>
                <button
                  onClick={() => setShowContactForm(false)}
                  className="text-[#254e70]/60 hover:text-[#254e70] transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleContactFormSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#254e70] mb-1">Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={contactForm.name}
                    onChange={handleContactFormChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#9dacff]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#254e70] mb-1">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={contactForm.email}
                    onChange={handleContactFormChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#9dacff]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#254e70] mb-1">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={contactForm.phone}
                    onChange={handleContactFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#9dacff]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#254e70] mb-1">Message *</label>
                  <textarea
                    name="message"
                    value={contactForm.message}
                    onChange={handleContactFormChange}
                    required
                    rows={4}
                    placeholder="Tell the therapist about what you're looking for and any questions you have..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#9dacff] resize-none"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowContactForm(false)}
                    className="flex-1 px-4 py-2 border border-[#254e70]/20 text-[#254e70] rounded-lg hover:bg-[#254e70]/5 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-[#254e70] text-white rounded-lg hover:bg-[#1d3e5a] transition-colors"
                  >
                    Send Message (Demo)
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default TherapistProfilePageDemo; 